import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { ClipboardList, Lock, Unlock } from "lucide-react";
import { DataTable } from "@/components/data-table/DataTable";
import type { ColumnDef } from "@/components/data-table/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type AssessmentStatus = "draft" | "complete" | "finalized";

interface PortfolioAssessment {
  id: string;
  workspaceId: string;
  companyName: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  status: AssessmentStatus;
  locked: boolean;
  completenessPercent: number | null;
  updatedAt: string;
}

interface AssessmentsResponse {
  data: PortfolioAssessment[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface ActiveCompany {
  workspaceId: string | null;
  companyName: string;
}

async function fetchAssessments(page: number, workspaceId: string, status: string): Promise<AssessmentsResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: "10" });
  if (workspaceId !== "all") params.set("workspaceId", workspaceId);
  if (status !== "all") params.set("status", status);
  const res = await fetch(`/api/portfolio/assessments?${params}`, { credentials: "include" });
  if (res.status === 403) throw new Error("This company is not an active company in your portfolio.");
  if (!res.ok) throw new Error("Failed to load assessments. Please refresh.");
  return res.json();
}

async function fetchActiveCompanies(): Promise<ActiveCompany[]> {
  const res = await fetch("/api/portfolio/companies?state=active", { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch companies");
  return res.json();
}

const STATUS_BADGES: Record<AssessmentStatus, { label: string; className: string; dot: string }> = {
  draft: { label: "Draft", className: "bg-slate-100 text-slate-600", dot: "bg-slate-400" },
  complete: { label: "Complete", className: "bg-blue-50 text-blue-600", dot: "bg-blue-500" },
  finalized: { label: "Finalised", className: "bg-emerald-50 text-emerald-600", dot: "bg-emerald-500" },
};

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

export default function PortfolioAssessments() {
  const [, setLocation] = useLocation();
  // The company filter lives in the URL (?workspaceId=) so Companies rows can deep-link here
  // and the sidebar link (no query) resets it.
  const companyFilter = new URLSearchParams(useSearch()).get("workspaceId") ?? "all";
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["/api/portfolio/assessments", page, companyFilter, statusFilter],
    queryFn: () => fetchAssessments(page, companyFilter, statusFilter),
  });

  // Same key shape as PortfolioCompanies (search "", state "active") so the cache is shared.
  const { data: companies } = useQuery({
    queryKey: ["/api/portfolio/companies", "", "active"],
    queryFn: fetchActiveCompanies,
  });

  const handleCompanyChange = (value: string) => {
    setPage(1);
    setLocation(value === "all" ? "/portfolio/assessments" : `/portfolio/assessments?workspaceId=${value}`, {
      replace: true,
    });
  };

  const columns: ColumnDef<PortfolioAssessment>[] = [
    {
      id: "company",
      header: "Company",
      cell: (a) => <span className="font-semibold text-slate-900">{a.companyName}</span>,
    },
    {
      id: "name",
      header: "Assessment",
      cell: (a) => <span className="text-slate-700">{a.name}</span>,
    },
    {
      id: "period",
      header: "Period",
      cell: (a) => (
        <span className="text-sm text-slate-500">{`${formatDate(a.periodStart)} - ${formatDate(a.periodEnd)}`}</span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: (a) => {
        const badge = STATUS_BADGES[a.status];
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${badge.className}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
            {badge.label}
          </span>
        );
      },
    },
    {
      id: "locked",
      header: "Locked",
      cell: (a) =>
        a.locked ? (
          <Lock className="h-4 w-4 text-amber-500" aria-label="Locked" />
        ) : (
          <Unlock className="h-4 w-4 text-slate-400" aria-label="Not locked" />
        ),
    },
    {
      id: "completeness",
      header: "Completeness",
      cell: (a) => {
        const percent = a.completenessPercent ?? 0;
        return (
          <div className="flex items-center gap-2">
            <div className="w-16 bg-slate-100 rounded-full h-2">
              <div className="bg-primary h-2 rounded-full" style={{ width: `${percent}%` }} />
            </div>
            <span className="text-sm font-medium text-slate-700">{percent}%</span>
          </div>
        );
      },
    },
    {
      id: "updated",
      header: "Updated",
      cell: (a) => <span className="text-sm text-slate-500">{formatDate(a.updatedAt)}</span>,
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">Assessments</h1>
        <p className="text-slate-500 mt-2">All reporting periods across your active companies</p>
      </div>

      <div className="bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-5 border-b border-border/30">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">{`Assessments (${data?.total ?? 0})`}</h3>
          </div>
        </div>
        <div className="p-6">
          <DataTable
            columns={columns}
            data={data?.data ?? []}
            getRowId={(a) => a.id}
            isLoading={isLoading}
            emptyState={
              isError
                ? { icon: ClipboardList, title: error?.message ?? "Failed to load assessments. Please refresh." }
                : { icon: ClipboardList, title: "No assessments found" }
            }
            filters={
              <>
                <Select value={companyFilter} onValueChange={handleCompanyChange}>
                  <SelectTrigger className="w-[220px] rounded-2xl bg-slate-50">
                    <SelectValue placeholder="Filter by company" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All companies</SelectItem>
                    {companies
                      ?.filter((c) => c.workspaceId)
                      .map((c) => (
                        <SelectItem key={c.workspaceId} value={c.workspaceId!}>
                          {c.companyName}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Select
                  value={statusFilter}
                  onValueChange={(value) => {
                    setStatusFilter(value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[160px] rounded-2xl bg-slate-50">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                    <SelectItem value="finalized">Finalised</SelectItem>
                  </SelectContent>
                </Select>
              </>
            }
            pagination={
              data
                ? { page, pageSize: data.pageSize, total: data.total, totalPages: data.totalPages, onPageChange: setPage }
                : undefined
            }
            onRowClick={(a) => setLocation(`/portfolio/assessments/${a.id}`)}
          />
        </div>
      </div>
    </div>
  );
}
